/**
 * How much a person sees (ADR 0019): `settings` — how much Drang shows, everywhere (Settings, dashboards, the
 * assistant, …); `panel` — what the task panel shows: the same, unless the person set the panel's own level
 * (`panelOwn`, UI cleanup U5). Both belong to the person, the same on every device and in every space.
 */
import { useCallback } from "react";
import type { Level } from "../core/schema";
import { useSession } from "./session";

export interface Levels {
  settings: Level;
  panel: Level;
  /** the task panel's own level, or null: it follows `settings` */
  panelOwn?: Level | null;
}

// Expert for both (ADR 0019, amended 2026-10-24)
const DEFAULT: Levels = { settings: "power", panel: "power", panelOwn: null };

export function useLevels(): Levels & {
  set(which: "settings" | "panel", level: Level | null): Promise<void>;
} {
  const levels = useSession((s) => s.session?.profile?.levels) ?? DEFAULT;
  const set = useCallback(async (which: "settings" | "panel", level: Level | null) => {
    const apply = (next: Levels) =>
      useSession.setState((st) =>
        st.session?.profile
          ? { session: { ...st.session, profile: { ...st.session.profile, levels: next } } }
          : st,
      );
    const before = useSession.getState().session?.profile?.levels ?? DEFAULT;
    // at once; the server confirms or the old value comes back
    apply(
      which === "settings"
        ? { ...before, settings: level ?? before.settings, panel: before.panelOwn ?? level ?? before.panel }
        : { ...before, panelOwn: level, panel: level ?? before.settings },
    );
    const r = await fetch("/api/internal/session/levels", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [which]: level }),
    }).catch(() => null);
    if (r?.ok) apply(((await r.json()) as { levels: Levels }).levels);
    else apply(before);
  }, []);
  return { ...levels, set };
}
